/**
 * Admin → Orders.
 *
 * Paginated table of every order. Filter by status / report / coupon.
 * Per-row "Resend PDF" button to re-trigger delivery on stuck orders.
 * CSV download for accounting.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { adminApi } from '@/api/adminStorefront';
import type { OrderRow } from '@/api/adminStorefront';
import { AccountMenu } from '@/components/AccountMenu';

const STATUSES = ['', 'pending', 'paid', 'delivered', 'failed', 'refunded'] as const;

export function OrdersAdminPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [reportSlug, setReportSlug] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const ordersQ = useQuery({
    queryKey: ['admin', 'orders', statusFilter, reportSlug, couponCode, offset],
    queryFn: () =>
      adminApi.listOrders({
        status: statusFilter || undefined,
        report_slug: reportSlug || undefined,
        coupon_code: couponCode || undefined,
        limit,
        offset,
      }),
  });

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 lg:px-8 lg:py-10">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <AdminBreadcrumb />
        <AccountMenu />
      </div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-saffron-100">
            {t('आदेश', 'Orders')}
          </h1>
          <p className="text-sm text-stone-400">
            {t(
              'सभी ख़रीदारी, स्थिति-वार। डिलीवरी फेल हुए आदेश यहाँ से रीसेंड करें।',
              'All purchases, by status. Resend stuck deliveries from here.',
            )}
          </p>
        </div>
        <a href="/api/v1/admin/orders.csv"
           className="rounded-md border border-saffron-600 px-3 py-2 text-sm text-saffron-200 hover:bg-saffron-700/30">
          {t('CSV डाउनलोड', 'Download CSV')}
        </a>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <FilterSelect label={t('स्थिति', 'Status')}
                      value={statusFilter}
                      onChange={(v) => { setStatusFilter(v); setOffset(0); }}
                      options={STATUSES.map((s) => ({ value: s, label: s || t('सभी', 'all') }))} />
        <FilterInput label={t('रिपोर्ट slug', 'Report slug')}
                     value={reportSlug}
                     onChange={(v) => { setReportSlug(v); setOffset(0); }} />
        <FilterInput label={t('कूपन कोड', 'Coupon code')}
                     value={couponCode}
                     onChange={(v) => { setCouponCode(v.toUpperCase()); setOffset(0); }} />
      </div>

      <div className="overflow-x-auto rounded-2xl bg-stone-900/60 ring-1 ring-stone-800">
        <table className="min-w-full text-sm">
          <thead className="text-stone-500">
            <tr className="border-b border-stone-800">
              <Th>{t('दिनांक', 'Date')}</Th>
              <Th>{t('रिपोर्ट', 'Report')}</Th>
              <Th>{t('ख़रीदार', 'Buyer')}</Th>
              <Th>{t('राशि', 'Amount')}</Th>
              <Th>{t('कूपन', 'Coupon')}</Th>
              <Th>{t('स्थिति', 'Status')}</Th>
              <Th>{t('कार्रवाई', 'Action')}</Th>
            </tr>
          </thead>
          <tbody>
            {ordersQ.data?.rows.map((o) => (
              <OrderTr key={o.id} order={o} onResent={() => ordersQ.refetch()} />
            ))}
            {ordersQ.data && ordersQ.data.rows.length === 0 && (
              <tr><Td colSpan={7} className="text-center text-stone-500">
                {t('कोई आदेश नहीं मिला।', 'No orders found.')}
              </Td></tr>
            )}
          </tbody>
        </table>
      </div>

      {ordersQ.data && (
        <div className="mt-4 flex items-center justify-between text-xs text-stone-400">
          <span>
            {t(
              `कुल ${ordersQ.data.total} आदेश — ${offset + 1}-${Math.min(offset + limit, ordersQ.data.total)}`,
              `${ordersQ.data.total} orders — showing ${offset + 1}-${Math.min(offset + limit, ordersQ.data.total)}`,
            )}
          </span>
          <div className="flex gap-2">
            <button type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    className="rounded-md border border-stone-700 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40">
              ← {t('पिछला', 'Prev')}
            </button>
            <button type="button"
                    disabled={offset + limit >= ordersQ.data.total}
                    onClick={() => setOffset(offset + limit)}
                    className="rounded-md border border-stone-700 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40">
              {t('अगला', 'Next')} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrderTr({ order, onResent }: { order: OrderRow; onResent: () => void }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const resend = useMutation({
    mutationFn: () => adminApi.resendOrder(order.id),
    onSuccess: onResent,
  });
  return (
    <tr className="border-b border-stone-900/60">
      <Td className="whitespace-nowrap text-xs text-stone-400">
        {new Date(order.created_at).toLocaleString()}
      </Td>
      <Td className="font-mono text-xs">{order.report_slug}</Td>
      <Td>
        <div className="text-stone-100">{order.buyer_name}</div>
        <div className="text-xs text-stone-500">{order.buyer_email}</div>
      </Td>
      <Td className="whitespace-nowrap">
        {order.final_price_inr_display}
        {order.discount_paise > 0 && (
          <span className="ml-1 text-xs text-stone-500">
            (-₹{(order.discount_paise / 100).toLocaleString()})
          </span>
        )}
      </Td>
      <Td className="text-xs">
        {order.coupon_code && (
          <>
            <div className="font-mono text-saffron-200">{order.coupon_code}</div>
            <div className="text-stone-500">{order.coupon_owner_name}</div>
          </>
        )}
      </Td>
      <Td>
        <StatusBadge status={order.status} lang={lang} />
        {order.delivery_attempts > 0 && (
          <div className="mt-1 text-xs text-stone-500">
            {t(`${order.delivery_attempts} प्रयास`, `${order.delivery_attempts} attempts`)}
          </div>
        )}
      </Td>
      <Td>
        {(order.status === 'paid' || order.status === 'delivered') && (
          <button type="button"
                  onClick={() => void resend.mutateAsync()}
                  disabled={resend.isPending}
                  className="rounded-md border border-saffron-600 px-2 py-1 text-xs text-saffron-200 hover:bg-saffron-700/30 disabled:opacity-50">
            {resend.isPending
              ? t('भेज रहे हैं…', 'Sending…')
              : t('रीसेंड', 'Resend')}
          </button>
        )}
      </Td>
    </tr>
  );
}

function StatusBadge({ status, lang }: { status: string; lang: 'hi' | 'en' }) {
  const map: Record<string, { hi: string; en: string; tone: string }> = {
    pending:   { hi: 'लंबित',     en: 'pending',   tone: 'bg-amber-900/40 text-amber-200 ring-amber-900' },
    paid:      { hi: 'भुगतान',    en: 'paid',      tone: 'bg-blue-900/40 text-blue-200 ring-blue-900' },
    delivered: { hi: 'भेजा गया',  en: 'delivered', tone: 'bg-emerald-900/40 text-emerald-200 ring-emerald-900' },
    failed:    { hi: 'विफल',      en: 'failed',    tone: 'bg-rose-900/40 text-rose-200 ring-rose-900' },
    refunded:  { hi: 'धन-वापसी',  en: 'refunded',  tone: 'bg-stone-800/40 text-stone-300 ring-stone-700' },
  };
  const m = map[status] ?? { hi: status, en: status, tone: 'bg-stone-800/40 text-stone-300 ring-stone-700' };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${m.tone}`}>
      {lang === 'hi' ? m.hi : m.en}
    </span>
  );
}

// ─── Primitives ────────────────────────────────────────────────────────

function FilterInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block space-y-1 text-xs text-stone-400">
      <span>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none focus:border-saffron-500"
      />
    </label>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <label className="block space-y-1 text-xs text-stone-400">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none focus:border-saffron-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">{children}</th>;
}

function Td({ children, className, colSpan }: { children: React.ReactNode; className?: string; colSpan?: number }) {
  return <td colSpan={colSpan} className={`px-3 py-2 align-top ${className ?? ''}`}>{children}</td>;
}

function AdminBreadcrumb() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  return (
    <nav className="mb-4 flex items-center gap-2 text-xs text-stone-500">
      <Link to="/workbench" className="hover:text-saffron-200">
        {t('वर्कबेंच', 'Workbench')}
      </Link>
      <span>/</span>
      <Link to="/workbench/admin/orders" className="text-saffron-300">
        {t('आदेश', 'Orders')}
      </Link>
      <span>·</span>
      <Link to="/workbench/admin/coupons" className="hover:text-saffron-200">
        {t('कूपन', 'Coupons')}
      </Link>
    </nav>
  );
}
